import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import { ExternalLink, MessageSquare } from "lucide-react";
import AppointmentSmsDialog from "@/components/appointment/AppointmentSmsDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { APT } from "@/constants/testIds";
import { cn } from "@/lib/utils";
import {
  appointmentNotes,
  appointmentStatusLabel,
  appointmentStatusTone,
  appointmentVisitType,
  confirmationStatusLabel,
  editAppointmentPath,
  formatAppointmentTime,
  providerDisplay,
  referringProviderDisplay,
  startEncounterFromAppointment,
  canStartEncounter,
} from "@/lib/appointmentApi";
import { encounterScreenPath } from "@/lib/patientChartNewEncounter";

const STATUS_STYLE = {
  booked: "bg-sky-100 text-sky-800",
  arrived: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-slate-200 text-slate-600",
  noshow: "bg-red-100 text-red-700",
};

function startEncounterHint(appointment) {
  if (appointment?.encounterId) {
    return "A clinical Encounter is already linked to this appointment.";
  }
  const status = appointmentStatusTone(appointment?.status);
  if (status === "cancelled" || status === "noshow") {
    return "Cancelled and no-show appointments do not create an Encounter.";
  }
  if (status === "booked") {
    return "Mark the appointment Arrived before starting a clinical Encounter.";
  }
  return "Saving or opening this appointment does not create an Encounter.";
}

const CONFIRM_STYLE = {
  confirmed: "bg-emerald-100 text-emerald-800",
  pending: "bg-orange-100 text-orange-800",
  unconfirmed: "bg-slate-100 text-slate-700",
};

function Detail({ label, children }) {
  return (
    <div>
      <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-0.5 text-sm text-slate-900">{children || "—"}</dd>
    </div>
  );
}

export default function AppointmentDetailDialog({
  appointment,
  patientLabel,
  locationName,
  dateLabel,
  onClose,
  onChanged,
  editOptions = {},
  canWriteAppointments = true,
  canWriteEncounters = true,
  canAccessPatients = true,
  canAccessEncounters = true,
  chrome = "default",
}) {
  const navigate = useNavigate();
  const calendarChrome = chrome === "calendar";
  const [starting, setStarting] = useState(false);
  const [smsOpen, setSmsOpen] = useState(false);
  const open = Boolean(appointment);

  useEffect(() => {
    setSmsOpen(false);
  }, [appointment?.id]);

  const startVisit = async () => {
    if (!appointment?.id) return;
    setStarting(true);
    try {
      const result = await startEncounterFromAppointment(appointment.id, {
        patientId: appointment.patientId,
      });
      toast.success(result.created ? "Encounter started." : "Opening existing encounter.");
      onChanged?.();
      onClose();
      const path = encounterScreenPath(result.encounter);
      if (path) navigate(path);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not start encounter.");
    } finally {
      setStarting(false);
    }
  };

  const openEncounter = () => {
    if (!appointment?.encounterId) return;
    onClose();
    navigate(`/encounters/${encodeURIComponent(appointment.encounterId)}`);
  };

  const editAppointment = () => {
    if (!appointment?.id) return;
    onClose();
    navigate(editAppointmentPath(appointment.id, editOptions));
  };

  return (
    <>
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        data-testid={APT.detail}
        className={cn("max-w-lg", calendarChrome && "emr-apt-dialog rounded-none")}
      >
        <DialogHeader>
          <DialogTitle>Appointment</DialogTitle>
          <DialogDescription>
            Scheduled event. An Encounter is created only when clinical care begins.
          </DialogDescription>
        </DialogHeader>

        {appointment ? (
          <dl className="grid grid-cols-2 gap-4">
            <Detail label="Patient">
              <div className="flex flex-col gap-1">
                <span className="font-medium">{patientLabel}</span>
                {appointment.patientId && canAccessPatients ? (
                  <Link
                    to={`/patients/${appointment.patientId}`}
                    className="inline-flex items-center gap-1 text-sm font-medium text-[#1E79E8] hover:underline"
                    data-testid={APT.viewChart}
                    onClick={onClose}
                  >
                    View patient chart <ExternalLink className="h-3 w-3" />
                  </Link>
                ) : null}
              </div>
            </Detail>
            <Detail label="When">
              {dateLabel} · {formatAppointmentTime(appointment.start)}
              {appointment.minutesDuration ? ` (${appointment.minutesDuration} min)` : ""}
            </Detail>
            <Detail label="Visit type">{appointmentVisitType(appointment) || "—"}</Detail>
            <Detail label="Location">{locationName || "—"}</Detail>
            <Detail label="Status">
              <Badge className={`${calendarChrome ? "rounded-none border-0 " : ""}${STATUS_STYLE[appointmentStatusTone(appointment.status)] || "bg-slate-100 text-slate-700"} hover:opacity-100`}>
                {appointment.statusLabel || appointmentStatusLabel(appointment.status)}
              </Badge>
            </Detail>
            <Detail label="Confirmation">
              <Badge
                className={`${calendarChrome ? "rounded-none border-0 " : ""}${CONFIRM_STYLE[appointment.confirmationStatus] || "bg-slate-100 text-slate-700"} hover:opacity-100`}
              >
                {confirmationStatusLabel(appointment.confirmationStatus)}
              </Badge>
            </Detail>
            <Detail label="Provider">{providerDisplay(appointment) || "—"}</Detail>
            <Detail label="Referring provider">{referringProviderDisplay(appointment) || "—"}</Detail>
            <div className="col-span-2">
              <Detail label="Notes">{appointmentNotes(appointment) || "—"}</Detail>
            </div>
          </dl>
        ) : null}

        <DialogFooter className="gap-2 sm:justify-between">
          <p className="text-xs text-slate-500">{startEncounterHint(appointment)}</p>
          <div className="flex flex-wrap gap-2">
            {canWriteAppointments ? (
            <Button
              type="button"
              variant="outline"
              data-testid={APT.sendSms}
              className={calendarChrome ? "h-9 rounded-none" : undefined}
              onClick={() => setSmsOpen(true)}
            >
              <MessageSquare className="h-4 w-4" />
              Send SMS
            </Button>
            ) : null}
            {canWriteAppointments ? (
            <Button
              variant="outline"
              data-testid={APT.editBtn}
              className={calendarChrome ? "h-9 rounded-none" : undefined}
              onClick={editAppointment}
            >
              Edit Appointment
            </Button>
            ) : null}
            {appointment?.encounterId && canAccessEncounters ? (
              <Button
                data-testid={APT.openEncounter}
                className={calendarChrome ? "h-9 rounded-none bg-teal-700 hover:bg-teal-800" : "bg-[#0d5c4d] hover:bg-[#0a4b3f]"}
                onClick={openEncounter}
              >
                Open Encounter
              </Button>
            ) : canWriteEncounters && canStartEncounter(appointment) ? (
              <Button
                data-testid={APT.startEncounter}
                className={calendarChrome ? "h-9 rounded-none bg-teal-700 hover:bg-teal-800" : "bg-[#0d5c4d] hover:bg-[#0a4b3f]"}
                disabled={starting}
                onClick={startVisit}
              >
                {starting ? "Starting…" : "Start Encounter"}
              </Button>
            ) : null}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
      <AppointmentSmsDialog
        appointment={appointment}
        open={smsOpen && open}
        onClose={() => setSmsOpen(false)}
      />
    </>
  );
}
